import React, { useMemo, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import PagerView from 'react-native-pager-view';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Snackbar, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';
import { useAuth } from '../context/AuthContext';
import JobService from '../services/jobService';
import TruckService from '../services/truckService';
import JobQueueCard from '../components/JobQueueCard';
import JobSummaryCard from '../components/JobSummaryCard';
import JobCard from '../components/JobCard';
import JobSection from '../components/JobSection';
import PickUpJobSheet from '../components/PickUpJobSheet';
import CompleteJobSheet from '../components/CompleteJobSheet';
import ReleaseJobDialog from '../components/ReleaseJobDialog';
import { isSameLocalDay } from '../utils/dateRange';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import FloatingActionButton from '../components/FloatingActionButton';
import AddJobBottomSheet from '../components/AddJobBottomSheet';
import RecordCompletedJobBottomSheet from '../components/RecordCompletedJobBottomSheet';
import { Job } from '../types/Job';
import { Truck } from '../types/Truck';
import { formatAmount } from '../utils/formatAmount';
import { ErrorLogger } from '../utils/errorLogger';

type TabView = 'queue' | 'history';
type PaymentFilter = 'all' | 'cash' | 'debt';

const JobsScreen: React.FC = () => {
  const theme = useTheme();
  const { user, role, userProfile } = useAuth();
  const isSupervisor = role === 'supervisor';
  // A supervisor with an assigned vehicle also drives: their Job Queue becomes
  // the driver board (pool / pick up / active job / complete).
  const canDrive = isSupervisor && !!userProfile?.assignedTruckId;

  const [activeTab, setActiveTab] = useState<TabView>('queue');
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('all');
  const [filterTruckId, setFilterTruckId] = useState<string | null>(null);
  const [addJobVisible, setAddJobVisible] = useState(false);
  const [recordJobVisible, setRecordJobVisible] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Driving-supervisor state (only used when canDrive).
  const [pickupJob, setPickupJob] = useState<Job | null>(null);
  const [pickupSheetVisible, setPickupSheetVisible] = useState(false);
  const [completeSheetVisible, setCompleteSheetVisible] = useState(false);
  const [releaseDialogVisible, setReleaseDialogVisible] = useState(false);

  const { data: allJobs } = useFirestoreSubscription(
    (onUpdate, onError) => JobService.subscribeToAllJobs(onUpdate, onError),
    [] as Job[],
  );

  const { data: trucks } = useFirestoreSubscription(
    (onUpdate, onError) => TruckService.subscribeToAllTrucks(onUpdate, onError),
    [] as Truck[],
  );

  const truckMap = useMemo(() => {
    const map = new Map<string, string>();
    trucks.forEach(t => { if (t.id) map.set(t.id, t.plateNumber); });
    return map;
  }, [trucks]);

  const byNewestFirst = (a: Job, b: Job) => b.createdAt.getTime() - a.createdAt.getTime();

  const now = useMemo(() => new Date(), []);

  const pendingJobs = useMemo(
    () => allJobs.filter(j => j.status === 'pending').sort(byNewestFirst),
    [allJobs],
  );

  const assignedJobs = useMemo(
    () => allJobs.filter(j => j.status === 'assigned').sort(byNewestFirst),
    [allJobs],
  );

  // A driving supervisor's own active job (they can work one at a time).
  const myActiveJob = useMemo(
    () =>
      canDrive
        ? assignedJobs.find(j => j.assignedDriverId === user?.uid) ?? null
        : null,
    [canDrive, assignedJobs, user?.uid],
  );

  const completedJobs = useMemo(
    () => allJobs.filter(j => j.status === 'completed').sort(byNewestFirst),
    [allJobs],
  );

  // Strict on completedAt: legacy completed jobs (pre-schema-update) lack
  // the field; rather than falling back to updatedAt, we hide them from the
  // "Completed today" section. They still surface in the History tab.
  const completedToday = useMemo(
    () =>
      completedJobs.filter(j =>
        j.completedAt != null && isSameLocalDay(j.completedAt, now),
      ),
    [completedJobs, now],
  );

  const truckIdsWithJobs = useMemo(() => {
    const ids = completedJobs
      .map(j => j.assignedTruckId)
      .filter((id): id is string => Boolean(id));
    return Array.from(new Set(ids));
  }, [completedJobs]);

  const displayCompletedJobs = useMemo(() => {
    let result = completedJobs;
    if (paymentFilter !== 'all') result = result.filter(j => j.paymentType === paymentFilter);
    if (filterTruckId !== null) result = result.filter(j => j.assignedTruckId === filterTruckId);
    return result;
  }, [completedJobs, paymentFilter, filterTruckId]);

  const pagerRef = useRef<PagerView>(null);
  const useSwipePager = Platform.OS !== 'web' && isSupervisor;

  const navigateToTab = (t: TabView) => {
    if (!isSupervisor) return;
    setActiveTab(t);
    if (useSwipePager) {
      pagerRef.current?.setPage(t === 'queue' ? 0 : 1);
    }
  };

  const handleDelete = (job: Job) => {
    setJobToDelete(job);
    setDeleteDialogVisible(true);
  };

  const confirmDelete = async () => {
    if (!jobToDelete?.id) return;
    try {
      setDeleting(true);
      await JobService.deleteJob(jobToDelete.id);
      setDeleteDialogVisible(false);
      setJobToDelete(null);
      setSnackMessage('Job deleted');
    } catch (error) {
      ErrorLogger.logError(error as any, 'JobsScreen.confirmDelete');
      setDeleteDialogVisible(false);
      setJobToDelete(null);
      setSnackMessage('Failed to delete job');
    } finally {
      setDeleting(false);
    }
  };

  // --- Driving-supervisor actions (only reachable when canDrive) ---
  const handlePickUpPress = (job: Job) => {
    setPickupJob(job);
    setPickupSheetVisible(true);
  };

  const handleConfirmPickUp = async () => {
    if (!pickupJob?.id || !user?.uid || !userProfile?.assignedTruckId) return;
    try {
      await JobService.pickUpJob(
        pickupJob.id,
        user.uid,
        userProfile.assignedTruckId,
        user.displayName ?? '',
      );
      setPickupSheetVisible(false);
      setPickupJob(null);
      setSnackMessage('Job picked up!');
    } catch (error) {
      ErrorLogger.logError(error as any, 'JobsScreen.handleConfirmPickUp');
      setPickupSheetVisible(false);
      setSnackMessage('Failed to pick up job.');
    }
  };

  const handleComplete = async (paymentType: 'cash' | 'debt', serviceCost: number) => {
    if (!myActiveJob?.id || !user?.uid) return;
    try {
      await JobService.completeJob(
        myActiveJob.id,
        paymentType,
        serviceCost,
        user.uid,
        user.displayName ?? '',
      );
      setCompleteSheetVisible(false);
      setSnackMessage('Job completed!');
    } catch (error) {
      ErrorLogger.logError(error as any, 'JobsScreen.handleComplete');
      setCompleteSheetVisible(false);
      setSnackMessage('Failed to complete job.');
    }
  };

  const handleRelease = async () => {
    if (!myActiveJob?.id) return;
    try {
      await JobService.releaseJob(myActiveJob.id);
      setReleaseDialogVisible(false);
      setSnackMessage('Job released.');
    } catch (error) {
      ErrorLogger.logError(error as any, 'JobsScreen.handleRelease');
      setReleaseDialogVisible(false);
      setSnackMessage('Failed to release job.');
    }
  };

  const renderEmpty = (icon: string, title: string, message: string, testID: string) => (
    <View testID={testID} style={styles.emptyState}>
      <MaterialCommunityIcons name={icon} size={64} color={theme.colors.outlineVariant} />
      <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>{title}</Text>
      <Text style={[styles.emptyMessage, { color: theme.colors.onSurfaceVariant }]}>
        {message}
      </Text>
    </View>
  );

  const queuePage = (
    <View key="queue" style={styles.page}>
      <ScrollView
        style={styles.pageScroll}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.pageContent}
      >
        <JobQueueCard jobs={[...pendingJobs, ...assignedJobs]} todayCount={completedToday.length} />

        <JobSection
          title="Pending"
          count={pendingJobs.length}
          emptyMessage="No jobs waiting for dispatch."
        >
          {pendingJobs.length > 0
            ? pendingJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  onDelete={handleDelete}
                  onPickUp={canDrive ? handlePickUpPress : undefined}
                  pickUpDisabled={!!myActiveJob}
                />
              ))
            : null}
        </JobSection>

        <JobSection
          title="Assigned"
          count={assignedJobs.length}
          emptyMessage="No drivers actively running jobs."
        >
          {assignedJobs.length > 0
            ? assignedJobs.map(job => {
                const isMine = canDrive && job.id === myActiveJob?.id;
                return (
                  <JobCard
                    key={job.id}
                    job={job}
                    truckMap={truckMap}
                    onDelete={handleDelete}
                    onComplete={isMine ? () => setCompleteSheetVisible(true) : undefined}
                    onRelease={isMine ? () => setReleaseDialogVisible(true) : undefined}
                  />
                );
              })
            : null}
        </JobSection>

        <JobSection
          title="Completed today"
          count={completedToday.length}
          emptyMessage="No jobs wrapped up yet today."
        >
          {completedToday.length > 0
            ? completedToday.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  truckMap={truckMap}
                  onDelete={handleDelete}
                />
              ))
            : null}
        </JobSection>
      </ScrollView>
    </View>
  );

  const historyPage = isSupervisor ? (
    <View key="history" style={styles.page}>
      <ScrollView style={styles.pageScroll} showsVerticalScrollIndicator={false} contentContainerStyle={styles.pageContent}>
        <JobSummaryCard />
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterRow}
          contentContainerStyle={styles.filterRowContent}
        >
          {(['all', 'cash', 'debt'] as const).map(f => (
            <TouchableOpacity
              key={f}
              testID={`filter-pill-${f}`}
              onPress={() => setPaymentFilter(f)}
              style={[
                styles.pill,
                paymentFilter === f
                  ? { backgroundColor: theme.colors.primary }
                  : { backgroundColor: theme.colors.surfaceVariant },
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  { color: paymentFilter === f ? theme.colors.onPrimary : theme.colors.onSurfaceVariant },
                ]}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {truckIdsWithJobs.length > 0 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterRow}
            contentContainerStyle={styles.filterRowContent}
          >
            <TouchableOpacity
              testID="truck-pill-all"
              onPress={() => setFilterTruckId(null)}
              style={[
                styles.pill,
                filterTruckId === null
                  ? { backgroundColor: theme.colors.primary }
                  : { backgroundColor: theme.colors.surfaceVariant },
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  { color: filterTruckId === null ? theme.colors.onPrimary : theme.colors.onSurfaceVariant },
                ]}
              >
                All trucks
              </Text>
            </TouchableOpacity>
            {truckIdsWithJobs.map(truckId => (
              <TouchableOpacity
                key={truckId}
                testID={`truck-pill-${truckId}`}
                onPress={() => setFilterTruckId(truckId)}
                style={[
                  styles.pill,
                  filterTruckId === truckId
                    ? { backgroundColor: theme.colors.primary }
                    : { backgroundColor: theme.colors.surfaceVariant },
                ]}
              >
                <Text
                  style={[
                    styles.pillText,
                    { color: filterTruckId === truckId ? theme.colors.onPrimary : theme.colors.onSurfaceVariant },
                  ]}
                >
                  {truckMap.get(truckId) ?? truckId}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        {displayCompletedJobs.length === 0
          ? renderEmpty(
              'truck-fast-outline',
              'No completed jobs',
              'Completed jobs will appear here once drivers wrap up runs.',
              'empty-state',
            )
          : (
            <View style={styles.cardList}>
              {displayCompletedJobs.map(job => (
                <View key={job.id}>
                  <JobCard
                    job={job}
                    truckMap={truckMap}
                    onDelete={handleDelete}
                  />
                </View>
              ))}
            </View>
          )}
      </ScrollView>
    </View>
  ) : null;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text testID="jobs-screen-header" style={[styles.headerTitle, { color: theme.colors.onBackground }]}>
          Jobs
        </Text>
        {isSupervisor && (
          <View style={[styles.tabSwitcher, { backgroundColor: theme.colors.surfaceVariant }]}>
            {(['queue', 'history'] as const).map(t => (
              <TouchableOpacity
                key={t}
                testID={`tab-${t}`}
                onPress={() => navigateToTab(t)}
                style={[
                  styles.tabButton,
                  activeTab === t && { backgroundColor: theme.colors.surface },
                ]}
              >
                <Text
                  style={[
                    styles.tabLabel,
                    { color: activeTab === t ? theme.colors.onSurface : theme.colors.onSurfaceVariant },
                  ]}
                >
                  {t === 'queue' ? 'Job Queue' : 'History'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {useSwipePager ? (
        <PagerView
          ref={pagerRef}
          style={styles.pager}
          initialPage={0}
          onPageSelected={e => {
            const page = e.nativeEvent.position;
            setActiveTab(page === 0 ? 'queue' : 'history');
          }}
        >
          {queuePage}
          {historyPage}
        </PagerView>
      ) : (
        <>
          {activeTab === 'queue' && queuePage}
          {isSupervisor && activeTab === 'history' && historyPage}
        </>
      )}

      <FloatingActionButton
        onPress={() => activeTab === 'queue' ? setAddJobVisible(true) : setRecordJobVisible(true)}
      />

      <AddJobBottomSheet
        visible={addJobVisible}
        onClose={() => setAddJobVisible(false)}
        onSuccess={() => setSnackMessage('Job added!')}
      />

      <RecordCompletedJobBottomSheet
        visible={recordJobVisible}
        onClose={() => setRecordJobVisible(false)}
        onSuccess={() => setRecordJobVisible(false)}
      />

      <ConfirmDeleteDialog
        visible={deleteDialogVisible}
        onDismiss={() => { setDeleteDialogVisible(false); setJobToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Job"
        message="Are you sure you want to delete this job? This action cannot be undone."
        itemSummary={
          jobToDelete
            ? `${jobToDelete.from} → ${jobToDelete.to} • ${formatAmount(jobToDelete.amount)}`
            : undefined
        }
        loading={deleting}
      />

      {canDrive && (
        <>
          <PickUpJobSheet
            job={pickupJob}
            visible={pickupSheetVisible}
            onClose={() => {
              setPickupSheetVisible(false);
              setPickupJob(null);
            }}
            onConfirm={handleConfirmPickUp}
          />
          <CompleteJobSheet
            visible={completeSheetVisible}
            onClose={() => setCompleteSheetVisible(false)}
            onConfirm={handleComplete}
          />
          <ReleaseJobDialog
            visible={releaseDialogVisible}
            onDismiss={() => setReleaseDialogVisible(false)}
            onConfirm={handleRelease}
          />
        </>
      )}

      <Snackbar
        visible={snackMessage.length > 0}
        onDismiss={() => setSnackMessage('')}
        duration={3000}
        action={{ label: 'OK', onPress: () => setSnackMessage('') }}
      >
        {snackMessage}
      </Snackbar>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  headerTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 22,
  },
  tabSwitcher: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 3,
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 6,
  },
  tabLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
  },
  pager: {
    flex: 1,
  },
  page: {
    flex: 1,
  },
  pageScroll: {
    flex: 1,
  },
  pageContent: {
    paddingBottom: 96,
  },
  cardList: {
    paddingHorizontal: 16,
    gap: 10,
  },
  filterRow: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  filterRowContent: {
    flexDirection: 'row',
    gap: 8,
  },
  pill: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 9999,
  },
  pillText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 48,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default JobsScreen;
